import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

type FullScreenContextProps = {
  isFullScreen: boolean;
  setIsFullScreen: React.Dispatch<React.SetStateAction<boolean>>;
};

const FullScreenContext = createContext<FullScreenContextProps | undefined>(
  undefined,
);

interface FullScreenProviderProps {
  children: React.ReactNode;
}

/** isFullScreen is only meaningful on these routes; leaving them via URL nav or back/forward must not leave a stale full-screen layout behind. */
const FULL_SCREEN_ELIGIBLE_PATHS = ["/ai-search", "/ai-search/results"];

const FullScreenProvider: React.FC<FullScreenProviderProps> = ({
  children,
}) => {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!FULL_SCREEN_ELIGIBLE_PATHS.includes(pathname)) {
      setIsFullScreen(false);
    }
  }, [pathname]);

  const value = useMemo(
    () => ({
      isFullScreen,
      setIsFullScreen,
    }),
    [isFullScreen],
  );

  return (
    <FullScreenContext.Provider value={value}>
      {children}
    </FullScreenContext.Provider>
  );
};

const useFullScreenContext = (): FullScreenContextProps => {
  const context = useContext(FullScreenContext);
  if (!context) {
    throw new Error(
      "useFullScreenContext must be used within a FullScreenProvider",
    );
  }
  return context;
};

export { FullScreenProvider, useFullScreenContext };
